import { Injectable } from '@angular/core';
import { gungComparatorHelper } from 'gung-common';
import {
  ConfigBaseFilter,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  SimpleConfigBaseFilter
} from 'gung-list';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { EdiRespondeOrder } from '../../models/edi';
import { BaseViewConfigService } from '../base-view-config/base-view-config.service';
import { EdiService } from '../edi/edi.service';
import { EdiMinDateConfigService } from '../edi-min-date-config.service';
import { format } from 'date-fns';
import {
  BuyerEdiOrderFilter,
  ConfigIdFilter,
  DeliveryEdiOrderFilter,
  SupplierEdiOrderFilter,
  ValidationFilter
} from '../edi-order-list-config/edi-order-list-config.service';

export class EdiOrderResponseSendStatusFilter extends SimpleConfigBaseFilter<EdiRespondeOrder> {
  constructor() {
    super();
  }
  getName(): string {
    return 'INVOICE_STATUS';
  }
  getOptionIds(item: EdiRespondeOrder): string[] {
    return [item.gungEdiMessageSendStatus || 'UNDEFINED'];
  }
  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EdiOrderResponseListConfigService implements EdiMinDateConfigService<EdiRespondeOrder> {
  protected minDateSubject = new BehaviorSubject<string>(this.getDefaultMinDate());
  protected glnNameMap: Record<string, string> = {};

  constructor(protected ediService: EdiService, protected baseViewConfigService: BaseViewConfigService) {
    this.ediService.getGlnNumbers().subscribe(glnNumbers => {
      glnNumbers.forEach(entry => {
        this.glnNameMap[String(entry.gln)] = `${entry.gln} - ${entry.b24.name} - ${entry.companyId}`;
      });
    });
  }

  getDefaultMinDate(): string {
    const date = new Date();
    date.setMonth(date.getMonth() - 2);
    return format(date, 'yyyy-MM-dd');
  }

  setMinDate(minDate: string): void {
    this.minDateSubject.next(minDate);
  }

  getMinDate(): string {
    return this.minDateSubject.getValue();
  }

  getItems(): Observable<EdiRespondeOrder[]> {
    return this.minDateSubject.pipe(
      switchMap(minDate => this.ediService.getOrderResponses(minDate)),
      map(items => items.sort((a, b) => gungComparatorHelper([a.timestamp, a.id], [b.timestamp, b.id], -1)))
    );
  }

  getFilters(): ConfigBaseFilter<EdiRespondeOrder>[] {
    return [
      new ValidationFilter(),
      new EdiOrderResponseSendStatusFilter(),
      new ConfigIdFilter(),
      new SupplierEdiOrderFilter(this.glnNameMap),
      new DeliveryEdiOrderFilter(this.glnNameMap),
      new BuyerEdiOrderFilter(this.glnNameMap)
    ];
  }

  getSortOptions(): ListSortOption<EdiRespondeOrder>[] {
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<EdiRespondeOrder>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => this.baseViewConfigService.getEdiOrderResponseCardListComponent(),
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Edi Invoices grid'
      }
    ];
  }

  getSearchTerms(item: EdiRespondeOrder): string[] {
    const generalSearchTerms = [
      item.documentReferences?.supplierOrder?.id,
      item.documentReferences?.buyerOrder?.id,
      item.documentReferences?.invoice?.id,
      item.documentReferences?.invoiceOcr?.id,
      item.documentReferences?.despatchAdvice?.id,
      item.documentReferences?.parcel?.id
    ].filter(s => !!s);
    if (item.erpIds) {
      return [item.id, item.consigneeGln?.toString(), item.buyerGln?.toString()]
        .concat(item.erpIds)
        .concat(generalSearchTerms);
    }
    return [item.id, item.buyerGln?.toString(), item.consigneeGln?.toString()].concat(generalSearchTerms);
  }

  getItemId(item: EdiRespondeOrder): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }
}
